import { Injectable } from '@angular/core';
import FileSaver from 'file-saver';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { TranslatorPipe } from './translator.pipe';
import { GlobalConstants, ImageTypes } from 'src/app/services/global-constants';

@Injectable({
  providedIn: 'root',
})
export class ExportService {

  constructor() {
    pdfMake.vfs = pdfFonts.pdfMake.vfs;
    pdfMake.fonts = {
      DINNextLTArabic: {
        normal: GlobalConstants.apiURL + 'public/fonts/DINNextLTArabic-Regular.ttf',
        bold: GlobalConstants.apiURL + 'public/fonts/DINNextLTArabic-Bold.ttf',
        italics: GlobalConstants.apiURL + 'public/fonts/DINNextLTArabic-Light.ttf',
        bolditalics:  GlobalConstants.apiURL + 'public/fonts/DINNextLTArabic-Heavy.ttf'
    }
   }
  }

  public cleanData(data: Array<any>): Array<any> {
    let cleanedData: Array<any> = [];
    data.forEach((obj: any) => {
      let newObj: any = {};
      for (let key in obj) {
        if (key === 'Action' || key === 'ID') {
          continue;
        } else {
          newObj[key] = obj[key].n?.replaceAll("&nbsp;", " ");
        }
      }
      cleanedData.push(newObj);
    });
    return cleanedData;
  }

  translator: TranslatorPipe = new TranslatorPipe()
  translate(key: string) {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }



  public cleanRowData(data: Array<any>): Array<any> {
    let cleanedData: Array<any> = [];
    data.forEach((obj: any) => {
      let newObj: any = {};
      for (let key in obj) {
        if (key === 'Action' || key === 'ID') {
          continue;
        } else {
          newObj[key] = obj[key];
        }
      }
      cleanedData.push(newObj);
    });
    return cleanedData;
  }

  exportToPdf(data: Array<any>, title:string = '', pageOrientation:any = 'portrait'): any {
    if (!(data.length > 0)) {
      return;
    }
    const tableBody: any[] = [];
    const headers = Object.keys(data[0]);
    const headerRow = headers.map((header) => ({ text: header, bold: true, noWrap: true }));
    tableBody.push(headerRow);
    data.forEach((item) => {
      const row = headers.map((header) => item[header] || '-');
      tableBody.push(row);
    });
    const content: TDocumentDefinitions = {
      pageOrientation: pageOrientation,
      content: [
        { text: title, style: 'header', alignment: 'center', margin: [0, 0, 0, 50] },
        {
          table: {
            headerRows: 1,
            widths: new Array(headers.length).fill('auto'),
            body: tableBody,
          },
          layout: 'lightHorizontalLines',
          alignment: 'center',
        },
      ],
      defaultStyle: {
        font: 'DINNextLTArabic'
      },
      styles: {
        header: { fontSize: 18, bold: true },
      },
    };
    pdfMake
      .createPdf(content)
      .download(title + new Date().getTime() + '.pdf');
  }

  exportExcelOutside(data: Array<any>) {
    let cleanedData = this.cleanRowData(data);
    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(cleanedData);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      this.saveAsExcelFile(excelBuffer, 'users');
    });
  }



  exportExcel(data: Array<any>) {
    let cleanedData = this.cleanData(data);

    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(cleanedData);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      this.saveAsExcelFile(excelBuffer, 'users');
    });
  }
  exportContentUpdateExcel(data: Array<any>, fileName?: string) {
    let cleanedData = data;

    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(cleanedData);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      if (fileName) {
        this.saveAsExcelFile(excelBuffer, fileName);
      } else {
        this.saveAsExcelFile(excelBuffer);
      }

    });
  }

    exportContentUpdateCSV(updatedDataArray: any, fileName?: string) {
      if (!updatedDataArray || updatedDataArray.length === 0) return;

      const columns = Object.keys(updatedDataArray[0]);
      let csvContent = '\uFEFF'; // Add BOM for UTF-8 encoding
      csvContent += columns.join(',') + '\n';

      updatedDataArray.forEach((row: any) => {
      csvContent += columns.map(col => {
        let value = row[col];
        if (typeof value === 'string') {
        value = value.replaceAll('&nbsp;', ' ');
        }
        // Escape double quotes and wrap value in quotes if it contains comma or newline
        if (typeof value === 'string' && (value.includes(',') || value.includes('\n') || value.includes('"'))) {
        value = `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const downloadFileName = fileName ? fileName : 'export_' + new Date().getTime();
      FileSaver.saveAs(blob, downloadFileName + '.csv');
    }

  private saveAsExcelFile(buffer: any, fileName?: string): void {
    let EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    let EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    if (fileName) {
      FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
    } else {
      FileSaver.saveAs(data, 'export_' + new Date().toISOString() + EXCEL_EXTENSION);
    }

  }

  public extractColumns(obj: any): Array<any> {
    let columns: Array<any> = [];

    for (let key of Object.keys(obj)) {
      columns.push({
        title: key.toUpperCase(),
        dataKey: key,
      });
    }
    return columns;
  }

  public exportToCsv(arr: Array<any>) {
    const columns = this.extractColumns(arr[0]);
    let data = '';

    columns.forEach((c) => {
      data += c.title + ',';
    });
    data += '\n';

    arr.forEach((e) => {
      Object.values(e).forEach((v: any) => {
        data += v?.replaceAll("&nbsp;", " ") + ',';
      });
      data += '\n';
    });
    const blob = new Blob([data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'export_' + new Date().getTime());
    link.click();

    // Clean up the URL object to avoid memory leaks
    window.URL.revokeObjectURL(url);
  }
}